import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Upload, Download, Trash2, X, Camera, Eye, Loader2, Paperclip } from 'lucide-react';
import { uploadAttachment, getAttachmentsByEntity, downloadAttachment, downloadThumbnail, deleteAttachment } from '../../services/api';

function resizeImageFile(file, maxDim = 1200, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > height && width > maxDim) {
        height = Math.round((height / width) * maxDim); width = maxDim;
      } else if (height > maxDim) {
        width = Math.round((width / height) * maxDim); height = maxDim;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => blob ? resolve(new File([blob], file.name, { type: 'image/jpeg' })) : reject(new Error('toBlob failed')),
        'image/jpeg', quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
    img.src = url;
  });
}

// === JOB ATTACHMENTS MODAL (with image preview) ===


export function JobAttachmentsModal({ isOpen, onClose, descriptionId }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const { data: attachments = [], refetch } = useQuery({
    queryKey: ['attachments', 'job_description', descriptionId],
    queryFn: () => getAttachmentsByEntity('job_description', descriptionId).then(r => r.data),
    enabled: isOpen && !!descriptionId
  });

  useEffect(() => {
    if (!attachments.length) return;
    attachments.forEach(att => {
      if (att.is_image && !thumbnailUrls[att.id]) {
        downloadThumbnail(att.id).then(res => {
          const url = URL.createObjectURL(res.data);
          setThumbnailUrls(prev => ({ ...prev, [att.id]: url }));
        }).catch(() => {});
      }
    });
  }, [attachments]);

  const handlePreview = async (att) => {
    if (!att.is_image) return;
    setPreviewLoading(true);
    try {
      const res = await downloadAttachment(att.id);
      const url = URL.createObjectURL(res.data);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
    } catch { toast.error('Failed to load preview'); }
    setPreviewLoading(false);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    let successCount = 0;
    let failCount = 0;
    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      const origName = file.name;
      setUploadProgress(`Processing ${i + 1} of ${files.length}: ${origName}`);
      const ext = origName.split('.').pop().toLowerCase();
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'heic', 'heif'];
      const isImage = file.type.startsWith('image/') || imageExts.includes(ext);
      const isHeic = ext === 'heic' || ext === 'heif' || file.type === 'image/heic' || file.type === 'image/heif';
      if (isImage && !isHeic) {
        try { file = await resizeImageFile(file, 1200, 0.7); } catch (e) { console.error('Image resize failed, using original:', e); }
      }
      setUploadProgress(`Uploading ${i + 1} of ${files.length}: ${origName}`);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'job_description');
      formData.append('entity_id', descriptionId);
      try { await uploadAttachment(formData); successCount++; } catch (err) { failCount++; toast.error(`Failed: ${origName}`); }
    }
    setUploading(false); setUploadProgress(''); refetch();
    if (successCount > 0) toast.success(`${successCount} file(s) uploaded`);
    if (failCount > 0 && successCount === 0) toast.error('All uploads failed. Try smaller files or a different format.');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleDownloadFile = async (att) => {
    try {
      const res = await downloadAttachment(att.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = att.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const [bulkDownloading, setBulkDownloading] = useState(false);
  const handleBulkDownload = async () => {
    if (!attachments.length) return;
    setBulkDownloading(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      for (const att of attachments) {
        try {
          const res = await downloadAttachment(att.id);
          zip.file(att.filename, res.data);
        } catch { /* skip failed files */ }
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attachments.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${attachments.length} file(s)`);
    } catch { toast.error('Bulk download failed'); }
    setBulkDownloading(false);
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  if (!isOpen) return null;

  return (
    <>
      {previewUrl && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-8" data-testid="job-image-preview-overlay" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <button className="absolute top-4 right-4 text-white"><X size={28} /></button>
          <img src={previewUrl} alt="Preview" className="max-w-full max-h-full object-contain rounded shadow-2xl" />
        </div>
      )}
      {previewLoading && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center"><Loader2 size={40} className="animate-spin text-white" /></div>
      )}

      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="job-attachments-modal">
        <div className="bg-white rounded-lg w-full max-w-lg max-h-[80vh] flex flex-col">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="font-semibold">Attachments</h2>
            <button onClick={onClose}><X size={20} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {uploading && (
              <div className="p-3 bg-blue-50 rounded text-sm text-blue-700 flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" />{uploadProgress}
              </div>
            )}
            {attachments.length === 0 && !uploading ? (
              <p className="text-slate-400 text-center py-8">No attachments yet</p>
            ) : attachments.map(att => (
              <div key={att.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded border" data-testid={`job-attachment-${att.id}`}>
                <div
                  className={`w-14 h-14 bg-slate-200 rounded overflow-hidden flex items-center justify-center flex-shrink-0 ${att.is_image ? 'cursor-pointer hover:ring-2 hover:ring-blue-400' : ''}`}
                  onClick={() => att.is_image && handlePreview(att)}
                >
                  {att.is_image && thumbnailUrls[att.id] ? (
                    <img src={thumbnailUrls[att.id]} alt="" className="w-full h-full object-cover" />
                  ) : <Paperclip size={20} className="text-slate-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{att.filename}</p>
                  <p className="text-xs text-slate-400">{formatSize(att.file_size)} {att.is_image && att.width ? `- ${att.width}x${att.height}` : ''}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {att.is_image && (
                    <button onClick={() => handlePreview(att)} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded" title="Preview"><Eye size={14} /></button>
                  )}
                  <button onClick={() => handleDownloadFile(att)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded"><Download size={14} /></button>
                  <button onClick={async () => { await deleteAttachment(att.id); refetch(); toast.success('Removed'); }} className="p-1.5 text-red-500 hover:bg-red-50 rounded" data-testid={`delete-job-att-${att.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="p-4 border-t flex justify-between items-center">
            <div className="flex items-center gap-2">
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="px-4 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-2" data-testid="job-upload-btn">
                <Upload size={16} /> Add File
              </button>
              <button onClick={() => cameraInputRef.current?.click()} disabled={uploading} className="px-4 py-2 text-sm bg-[#178c98] text-white rounded hover:bg-[#136e78] flex items-center gap-2" data-testid="job-take-photo-btn">
                <Camera size={16} /> Take Photo
              </button>
              {attachments.length > 0 && (
                <button onClick={handleBulkDownload} disabled={bulkDownloading} className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2" data-testid="bulk-download-btn">
                  {bulkDownloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  {bulkDownloading ? 'Zipping...' : `Download All (${attachments.length})`}
                </button>
              )}
              <input type="file" ref={fileInputRef} className="hidden" multiple accept="image/*,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx" onChange={handleFileChange} />
              <input type="file" ref={cameraInputRef} className="hidden" accept="image/*" capture="environment" onChange={handleFileChange} data-testid="job-camera-input" />
            </div>
            <button onClick={onClose} className="px-4 py-2 text-sm border rounded">Close</button>
          </div>
        </div>
      </div>
    </>
  );
}

// === MAIN JOB DETAIL COMPONENT ===
